module Client.Admin.EditUser.EditUser

open System
open Elmish
open Client
open Shared
open Illy.SafeStack
open Illy.SafeStack.Client
open Fable.React
open Fable.React.Props
open Shared.Admin.ConfigTypes
open Shared.Admin.EditUser

module DTO = 
    let blank = 
        {
            Forename = None
            Surname = None
            ActiveDirectory = None
            TelephoneNumber = None
            Email = None
            AdditionalText1 = None
        }

module AdditionalFieldsConfig = 
    let defaults = 
        {
            AdditionalText1Visible = false
            AdditionalText1Label = "Additional Text 1"
        }

module EditFields = 
    type Model = 
        {
            Forename: Text.Model
            Surname: Text.Model
            ActiveDirectory: Text.Model
            TelephoneNumber: Text.Model
            Email: Text.Model
            AdditionalText1Visible: bool
            AdditionalText1: Text.Model
        }
        
    type Msg = 
        | ForenameMsg of Text.Msg
        | SurnameMsg of Text.Msg
        | ActiveDirectoryMsg of Text.Msg
        | TelephoneNumberMsg of Text.Msg
        | EmailMsg of Text.Msg
        | AdditionalText1Msg of Text.Msg
 
    module Initialisations =
        let initForename value =
            Text.CustomSettings.defaultOptions()
            |> Text.init "Forename" "Forename" (Some "Enter Forename") 250. true value

        let initSurname value =
            Text.CustomSettings.defaultOptions()
            |> Text.init "Surname" "Surname" (Some "Enter Surname") 250. true value

        let initActiveDirectory value =
            Text.CustomSettings.defaultOptions()
            |> Text.init "ActiveDirectory" "active directory / network account" (Some "Enter active directory / network account") 250. false value

        let initTelephoneNumber value =
            Text.CustomSettings.defaultOptions()
            |> Text.init "TelephoneNumber" "Telephone Number" (Some "Enter Telephone Number") 50. false value

        let initEmail value =
            Text.CustomSettings.defaultOptions()
            |> Text.init "Email" "Email" (Some "Enter Email") 250. true value

        let initAdditionalText1 label value =
            Text.CustomSettings.defaultOptions()
            |> Text.init "AdditionalText1" label (Some (sprintf "Enter %s" label)) 50. false value

    let ofDTO (config: AdditionalFieldsConfig) (dto: DTO) = 
        {
            Forename = Initialisations.initForename dto.Forename
            Surname = Initialisations.initSurname dto.Surname
            ActiveDirectory = Initialisations.initActiveDirectory dto.ActiveDirectory
            TelephoneNumber = Initialisations.initTelephoneNumber dto.TelephoneNumber
            Email = Initialisations.initEmail dto.Email
            AdditionalText1Visible = config.AdditionalText1Visible
            AdditionalText1 = Initialisations.initAdditionalText1 config.AdditionalText1Label dto.AdditionalText1
        }

    let toDTO (model: Model) = 
        {
            Forename = Text.toString model.Forename
            Surname = Text.toString model.Surname
            ActiveDirectory = Text.toString model.ActiveDirectory
            TelephoneNumber = Text.toString model.TelephoneNumber
            Email = Text.toString model.Email
            AdditionalText1 = Text.toString model.AdditionalText1
        }

    module Validations = 
        open Illy.SafeStack.Validation
        open Shared.Admin.EditUser.Validations
        
        let resetValidations (model: Model) = 
            {model with 
                Forename = Text.resetValidations model.Forename
                Surname = Text.resetValidations model.Surname
                ActiveDirectory = Text.resetValidations model.ActiveDirectory
                TelephoneNumber = Text.resetValidations model.TelephoneNumber
                Email = Text.resetValidations model.Email
                AdditionalText1 = Text.resetValidations model.AdditionalText1
            }

        let dispatchValidations model = 
            let model = resetValidations model 
            function 
            | ValidationResult.Passed -> model 
            | ValidationResult.Warned _ -> failwith "Did not expect warnings"
            | ValidationResult.Failed (err, _) ->
                let errors = 
                    err 
                    |> ValidationInfrastructure.bundleValidations 
                        (fun x -> 
                            (match x with 
                            | ForenameValidations _ -> 0
                            | SurnameValidations _ -> 1
                            | ActiveDirectoryValidations _ -> 2
                            | TelephoneNumberValidations _ -> 3
                            | EmailValidations _ -> 4
                            | AdditionalText1Validations _ -> 5), EditUserValidations.toString x)
                        6
                {model with 
                    Forename = Text.setValidations model.Forename errors.[0] None
                    Surname = Text.setValidations model.Surname errors.[1] None
                    ActiveDirectory = Text.setValidations model.ActiveDirectory errors.[2] None
                    TelephoneNumber = Text.setValidations model.TelephoneNumber errors.[3] None
                    Email = Text.setValidations model.Email errors.[4] None
                    AdditionalText1 = Text.setValidations model.AdditionalText1 errors.[5] None
                }

    let update msg model = 
        match msg with 
        | ForenameMsg cMsg ->
            let m, c = Text.update cMsg model.Forename
            {model with Forename = m}, Cmd.map ForenameMsg c
        | SurnameMsg cMsg ->
            let m, c = Text.update cMsg model.Surname
            {model with Surname = m}, Cmd.map SurnameMsg c
        | ActiveDirectoryMsg cMsg ->
            let m, c = Text.update cMsg model.ActiveDirectory
            {model with ActiveDirectory = m}, Cmd.map ActiveDirectoryMsg c
        | TelephoneNumberMsg cMsg ->
            let m, c = Text.update cMsg model.TelephoneNumber
            {model with TelephoneNumber = m}, Cmd.map TelephoneNumberMsg c
        | EmailMsg cMsg ->
            let m, c = Text.update cMsg model.Email
            {model with Email = m}, Cmd.map EmailMsg c
        | AdditionalText1Msg cMsg ->
            let m, c = Text.update cMsg model.AdditionalText1
            {model with AdditionalText1 = m}, Cmd.map AdditionalText1Msg c

    let view loading model dispatch = 
        [
            yield div [
                ClassName "input-group fields-inline"
            ] [
                Text.text loading model.Forename (ForenameMsg >> dispatch)
                Text.text loading model.Surname (SurnameMsg >> dispatch)
            ]
            yield Text.text loading model.ActiveDirectory (ActiveDirectoryMsg >> dispatch)
            yield Text.text loading model.TelephoneNumber (TelephoneNumberMsg >> dispatch)
            yield Text.text loading model.Email (EmailMsg >> dispatch)
            if model.AdditionalText1Visible then 
                yield Text.text loading model.AdditionalText1 (AdditionalText1Msg >> dispatch)
        ]

type Model = 
    {
        Loading: bool
        UserId: UserId
        EditFields: EditFields.Model
        Saving: bool
    }

type Msg = 
    | ApiError of ApiError
    | SaveResult of FailureReason option
    | CloseOverlay  
    | SaveClicked 
    | InitDataLoaded of InitData
    | EditFieldsMsg of EditFields.Msg

type Event = 
    | CloseOverlay 
    | ApiError of ApiError 
    | SaveSuccessful of notification: string 

let init userId = 
    {
        Loading = true
        UserId = userId 
        EditFields = EditFields.ofDTO AdditionalFieldsConfig.defaults DTO.blank
        Saving = false
    }, Api.secureCall<IEditUserApi,_,_,_> SecuredRoute.Admin (fun api -> api.getInitData) userId InitDataLoaded Msg.ApiError

let update msg model = 
    match msg with 
    | InitDataLoaded d -> 
        {model with 
            EditFields = EditFields.ofDTO d.Config d.DTO
            Loading = false
        }, SideEffect.none
    | EditFieldsMsg eMsg ->
        let m, c = EditFields.update eMsg model.EditFields
        {model with EditFields = m}, Cmd.map EditFieldsMsg c |> SideEffect.Cmd
    | Msg.ApiError err -> model, err |> ApiError |> SideEffect.Event 
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event 
    | SaveClicked -> 
        let dto = EditFields.toDTO model.EditFields 
        let vResult = Validations.validateDTO dto
        if Validation.isSuccessful false vResult then 
            {model with Saving = true},
            Api.secureCall<IEditUserApi,_,_,_> SecuredRoute.Admin (fun api -> api.edit model.UserId) dto SaveResult Msg.ApiError
            |> SideEffect.Cmd
        else 
            {model with 
                EditFields = EditFields.Validations.dispatchValidations model.EditFields vResult
            }, SideEffect.none
    | SaveResult reason -> 
        match reason with 
        | None 
        | Some NothingToDo -> model, "User was updated successfully." |> SaveSuccessful |> SideEffect.Event
        | Some (FailedValidations v) -> 
            {model with 
                Saving = false
                EditFields = EditFields.Validations.dispatchValidations model.EditFields v
            }, SideEffect.none

let view model dispatch = 
    jQueryUI.modal "Edit User" 50 Msg.CloseOverlay dispatch (Some 800) [
        yield! EditFields.view model.Loading model.EditFields (EditFieldsMsg >> dispatch)
        yield! UI.saveControls (Some "Edit User") false model.Saving SaveClicked Msg.CloseOverlay dispatch
    ]